import { unzipSync } from "fflate"

/**
 * Memory-safe browser-side preview parser for BP OPSC (Operational Stock &
 * Sales Control) XLSX exports. Mirrors the authoritative Ruby semantics in
 * app/lib/bp_opsc_file_parser.rb:
 *
 *   - region            ← filename `Region (…)`
 *   - period (year/mon) ← the "Month : <Name> <YYYY>" row
 *   - 6MM range         ← the "6MM : From MM-YYYY to MM-YYYY" row
 *   - rowCount          ← data rows (after the header + serial-date row) that
 *                         carry any value in the 16 identity columns (A..P)
 *
 * Rows are located by CONTENT (the header row is the one whose cells include
 * "Region" and "Area"), not by fixed offset, because some exports prepend a
 * blank row #1. Tiny region files (e.g. Wipro Unza Indonesia ECOM, ~5.8 KB)
 * legitimately have 0 data rows and must parse without throwing.
 *
 * Streaming rationale (identical to xlsxPreviewParser.ts): LibreOffice Calc
 * writes every string inline (no xl/sharedStrings.xml), bloating a single RegCen
 * worksheet to ~20-24 MB compressed / far larger uncompressed. Decoding that
 * into one JS string risks V8's max string length, so worksheet bytes are
 * decoded in fixed windows and consumed one <row> at a time.
 */

export interface BpOpscPreview {
  region: string
  periodYear: number | null
  periodMonth: number | null
  monthRangeStart: string | null
  monthRangeEnd: string | null
  rowCount: number
}

const CHUNK_SIZE = 8 * 1024 * 1024
const IDENTITY_COLUMN_COUNT = 16

const MONTH_NAMES: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8,
  sep: 9, oct: 10, nov: 11, dec: 12,
}

export async function parseBpOpscForPreview(file: File): Promise<BpOpscPreview> {
  const buf = await file.arrayBuffer()
  const data = new Uint8Array(buf)
  const decode = (b: Uint8Array) => new TextDecoder().decode(b)

  const region = regionFromFilename(file.name)

  // Resolve worksheet paths (tab order) + extract shared strings & all sheets.
  const worksheetPaths = resolveWorksheetPaths(data, decode)
  const extracted = unzipSync(data, {
    filter: (f) =>
      f.name === "xl/sharedStrings.xml" ||
      (f.name.startsWith("xl/worksheets/") && f.name.endsWith(".xml")),
  })

  const sharedStrings: string[] = []
  const ssBytes = extracted["xl/sharedStrings.xml"]
  if (ssBytes) {
    const ssXml = decode(ssBytes)
    const parts = ssXml.split("<si>")
    for (let i = 1; i < parts.length; i++) {
      const matches = [...parts[i].matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
      sharedStrings.push(matches.map((m) => m[1]).join(""))
    }
  }

  const extractedWsNames = Object.keys(extracted).filter(
    (n) => n.startsWith("xl/worksheets/") && n.endsWith(".xml"),
  )
  const remainingPaths = extractedWsNames.filter((n) => !worksheetPaths.includes(n))
  const orderedPaths = [...worksheetPaths, ...remainingPaths]

  if (orderedPaths.length === 0) {
    throw new Error("Invalid XLSX: no worksheet paths found")
  }

  // Parse sheets; prefer the one where the header was located (real data sheet).
  let best: WorksheetResult | null = null
  let anyReadable = false
  for (const path of orderedPaths) {
    const wsBytes = extracted[path]
    if (!wsBytes) continue
    anyReadable = true
    const result = parseWorksheetBytes(wsBytes, sharedStrings)
    if (!best) {
      best = result
    } else if (result.headerFound && !best.headerFound) {
      best = result
    } else if (result.headerFound === best.headerFound && result.rowCount > best.rowCount) {
      best = result
    }
  }

  if (!anyReadable || !best) {
    throw new Error("Invalid XLSX: no readable worksheet found")
  }

  return {
    region,
    periodYear: best.periodYear,
    periodMonth: best.periodMonth,
    monthRangeStart: best.monthRangeStart,
    monthRangeEnd: best.monthRangeEnd,
    rowCount: best.rowCount,
  }
}

// ── Streaming worksheet parser ────────────────────────────────────────────────

interface WorksheetResult {
  headerFound: boolean
  periodYear: number | null
  periodMonth: number | null
  monthRangeStart: string | null
  monthRangeEnd: string | null
  rowCount: number
}

function parseWorksheetBytes(wsBytes: Uint8Array, sharedStrings: string[]): WorksheetResult {
  const decoder = new TextDecoder()
  let buffer = ""

  let headerFound = false
  let rowsAfterHeader = 0
  let rowCount = 0
  let periodYear: number | null = null
  let periodMonth: number | null = null
  let monthRangeStart: string | null = null
  let monthRangeEnd: string | null = null

  const processRow = (rowXml: string) => {
    if (!headerFound) {
      // Pre-header: locate the header row, and capture metadata text along the way.
      const cellStrings = rowCellStrings(rowXml, sharedStrings)
      if (cellStrings.includes("Region") && cellStrings.includes("Area")) {
        headerFound = true
        return
      }

      const text = rowJoinedText(rowXml, sharedStrings)
      if (!text) return

      if (periodMonth == null) {
        const m = text.match(/Month\s*:\s*(\w+)\s+(\d{4})/i)
        if (m) {
          periodYear = parseInt(m[2], 10)
          periodMonth = MONTH_NAMES[m[1].toLowerCase()] ?? null
        }
      }
      if (monthRangeStart == null) {
        const m = text.match(/6MM\s*:\s*From\s+(\d{1,2}-\d{4})\s+to\s+(\d{1,2}-\d{4})/i)
        if (m) {
          monthRangeStart = m[1]
          monthRangeEnd = m[2]
        }
      }
      return
    }

    // After the header, the first row is the serial-date row → skip it.
    rowsAfterHeader++
    if (rowsAfterHeader === 1) return

    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) return // blank row
    if (rowHasIdentity(rowXml, sharedStrings)) rowCount++
  }

  const flushRows = () => {
    let end: number
    while ((end = buffer.indexOf("</row>")) !== -1) {
      const start = buffer.lastIndexOf("<row ", end)
      if (start !== -1) processRow(buffer.slice(start + 5, end))
      buffer = buffer.slice(end + 6) // 6 === "</row>".length
    }
  }

  for (let off = 0; off < wsBytes.length; off += CHUNK_SIZE) {
    const isLast = off + CHUNK_SIZE >= wsBytes.length
    const slice = wsBytes.subarray(off, isLast ? wsBytes.length : off + CHUNK_SIZE)
    buffer += decoder.decode(slice, { stream: !isLast })
    flushRows()
  }
  flushRows()

  return { headerFound, periodYear, periodMonth, monthRangeStart, monthRangeEnd, rowCount }
}

// A data row counts if any of its first 16 (identity) columns carries a value.
function rowHasIdentity(rowXml: string, ss: string[]): boolean {
  for (const cellXml of rowXml.split("<c ").slice(1)) {
    const letterMatch = cellXml.match(/r="([A-Z]+)\d+"/)
    if (!letterMatch) continue
    if (columnLetterToIndex(letterMatch[1]) > IDENTITY_COLUMN_COUNT) continue
    const raw = xlsxCellValue(cellXml, ss)
    if (raw != null && raw.trim() !== "") return true
  }
  return false
}

// ── Cell / row helpers ────────────────────────────────────────────────────────

function rowCellStrings(rowXml: string, ss: string[]): string[] {
  const out: string[] = []
  for (const cellXml of rowXml.split("<c ").slice(1)) {
    const s = xlsxCellString(cellXml, ss)
    if (s != null) out.push(s.trim())
  }
  return out
}

function rowJoinedText(rowXml: string, ss: string[]): string | null {
  if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) return null
  const out: string[] = []
  for (const cellXml of rowXml.split("<c ").slice(1)) {
    const v = xlsxCellValue(cellXml, ss)
    if (v != null) out.push(v)
  }
  return out.length ? out.join(" ") : null
}

function columnLetterToIndex(letter: string): number {
  let result = 0
  for (const c of letter) result = result * 26 + (c.charCodeAt(0) - 64)
  return result
}

function regionFromFilename(filename: string): string {
  const m = filename.match(/Region\s*\(([^)]+)\)/i)
  return m ? m[1] : "Unknown"
}

// ── Workbook path resolution (identical strategy to xlsxPreviewParser) ─────────

function resolveWorksheetPaths(data: Uint8Array, decode: (b: Uint8Array) => string): string[] {
  const fallback = ["xl/worksheets/sheet1.xml"]
  try {
    const metaFiles = unzipSync(data, {
      filter: (f) =>
        f.name === "xl/workbook.xml" || f.name === "xl/_rels/workbook.xml.rels",
    })

    const wbBytes = metaFiles["xl/workbook.xml"]
    const relsBytes = metaFiles["xl/_rels/workbook.xml.rels"]
    if (!wbBytes || !relsBytes) return fallback

    const wbXml = decode(wbBytes)
    const relsXml = decode(relsBytes)

    const rIds: string[] = []
    for (const m of wbXml.matchAll(/<sheet\b[^>]*>/g)) {
      const idMatch = m[0].match(/\br:id="([^"]+)"/)
      if (idMatch) rIds.push(idMatch[1])
    }
    if (!rIds.length) return fallback

    const paths = rIds
      .map((rId) => extractRelTarget(relsXml, rId))
      .filter((t): t is string => t !== null)
      .map((target) => {
        if (target.startsWith("../")) return target.slice(3)
        if (target.startsWith("/")) return target.slice(1)
        return `xl/${target}`
      })

    return paths.length > 0 ? paths : fallback
  } catch {
    return fallback
  }
}

function extractRelTarget(relsXml: string, rId: string): string | null {
  const parts = relsXml.split(/<Relationship\b/)
  for (let i = 1; i < parts.length; i++) {
    if (parts[i].includes(`Id="${rId}"`)) {
      const m = parts[i].match(/\bTarget="([^"]+)"/)
      if (m) return m[1]
    }
  }
  return null
}

function xlsxCellString(cellXml: string, ss: string[]): string | null {
  if (cellXml.includes('t="s"')) {
    const m = cellXml.match(/<v>(\d+)<\/v>/)
    return m ? (ss[parseInt(m[1], 10)] ?? null) : null
  }
  if (cellXml.includes('t="inlineStr"')) {
    const matches = [...cellXml.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    return matches.length > 0 ? matches.map((m) => m[1]).join("") : null
  }
  if (cellXml.includes('t="str"')) {
    const m = cellXml.match(/<v>([^<]*)<\/v>/)
    return m ? m[1] : null
  }
  return null
}

function xlsxCellValue(cellXml: string, ss: string[]): string | null {
  if (cellXml.includes('t="inlineStr"')) {
    const matches = [...cellXml.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    return matches.length > 0 ? matches.map((m) => m[1]).join("") : null
  }
  if (cellXml.includes('t="s"')) {
    const m = cellXml.match(/<v>(\d+)<\/v>/)
    return m ? (ss[parseInt(m[1], 10)] ?? null) : null
  }
  if (cellXml.includes('t="str"')) {
    const m = cellXml.match(/<v>([^<]*)<\/v>/)
    return m ? m[1] : null
  }
  const m = cellXml.match(/<v>([^<]*)<\/v>/)
  return m ? m[1] : null
}
